import React, { useEffect } from 'react';
import { GamifyIcon } from 'application/components/GamifyIcon';
import {
  SectionTitle,
  InputBodyContainer,
  CustomInputLabel,
} from '../../GameFormComponents';
import styled from '@emotion/styled';
import NewGameState from '../../_types/NewGameState';
import RoundState from '../../_types/RoundState';

// Styled components
const ViewContainer = styled.div`
  margin-bottom: 24px;
`;

const CustomInputContainer = styled.div<{ theme?: any }>`
  width: 100%;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 53px;
  border: 1px solid ${({ theme }) => theme?.custom?.stroke || '#d7d9d6'};
  border-radius: 8px;
  background-color: ${({ theme }) => theme?.custom?.background1 || '#fff'};
  padding: 0 16px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  transition: border-color 0.2s ease;
  cursor: pointer;

  &:hover,
  &:focus-within {
    border-color: ${({ theme }) =>
      theme?.primary || 'var(--fi-accent, #ff6a00)'};
  }
`;

const LeftSection = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const IconContainer = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: #fff0e8;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const ContestantNumber = styled.input<{ theme?: any }>(({ theme }) => ({
  border: 'none',
  outline: 'none',
  fontSize: 16,
  fontWeight: 600,
  minWidth: 40,
  padding: 0,
  background: 'transparent',
  color: theme?.custom?.text || '#333',
  '&::placeholder': {
    color: theme?.custom?.textLight || '#757575',
    opacity: 0.7,
  },
}));

const RightSection = styled.div`
  display: flex;
  align-items: center;
  color: #555;
  font-weight: 600;
  font-size: 14px;
  white-space: nowrap;
  gap: 6px;
`;

interface Props {
  newGameState: NewGameState;
  onChangeNewGameState: (newGameState: NewGameState) => void;
}

const BracketView: React.FC<Props> = ({
  newGameState,
  onChangeNewGameState,
}) => {
  // Set up bracket data for pre-filled totalCompetitors in edit mode
  useEffect(() => {
    // Use setTimeout to ensure component is fully rendered before setup
    const timer = setTimeout(() => {
      // Check if we have totalCompetitors but no proper bracket setup
      if (
        newGameState.bracket?.totalCompetitors > 0 &&
        newGameState.contestType === 'bracket_manual' &&
        (!newGameState.totalRounds ||
          newGameState.totalRounds === 0 ||
          newGameState.bracket.bracket.length === 0)
      ) {
        console.log(
          'Setting up bracket with timeout',
          newGameState.bracket.totalCompetitors
        );
        setupBracketData(newGameState.bracket.totalCompetitors);
      }
    }, 300); // Small delay to ensure component is rendered

    return () => clearTimeout(timer);
  }, []);

  // Create default round state with auto-populated name and copy settings from previous round
  const createDefaultRoundState = (
    roundIdx: number,
    previousRound?: RoundState
  ): Partial<RoundState> => {
    const baseState = {
      bracketName: `Round ${roundIdx + 1}`,
      startDate: '',
      endDate: '',
      // Copy from previous round or use defaults
      useMultipleKPIs: previousRound?.useMultipleKPIs ?? false,
      KPIRuleID: previousRound?.KPIRuleID ?? '',
      KPIRuleName: previousRound?.KPIRuleName ?? '',
      tieKPIRuleID: previousRound?.tieKPIRuleID ?? '',
      tieKPIRuleName: previousRound?.tieKPIRuleName ?? '',
      isActionCountCustom: previousRound?.isActionCountCustom ?? false,
      actionCount: previousRound?.actionCount ?? '0',
      actionCountType: previousRound?.actionCountType ?? 'count',
      actionCountTypeNum: previousRound?.actionCountTypeNum ?? 0,
      // Copy mission rules from previous round
      missionRules: previousRound?.missionRules
        ? [...previousRound.missionRules]
        : [],
      rules: previousRound?.rules ? [...previousRound.rules] : [],
      // Reward settings (keep defaults but copy currency and type preferences)
      rewardDisposition: previousRound?.rewardDisposition ?? 'winner',
      rewardWhen: previousRound?.rewardWhen ?? 'mission',
      rewardType: previousRound?.rewardType ?? 'mission_reward',
      rewardCurrency: previousRound?.rewardCurrency ?? 'reward',
      // Keep reward IDs and names empty for new rounds
      rewardID1: '',
      rewardName1: '',
      rewardID2: '',
      rewardName2: '',
      rewardID3: '',
      rewardName3: '',
      rewardIDp: '',
      rewardNameP: '',
      showCustomReward1: false,
      showCustomReward2: false,
      showCustomReward3: false,
    };

    return baseState;
  };

  // Function to set up bracket data with a given number of competitors
  const setupBracketData = (numCompetitors: number) => {
    // Calculate rounds
    const largestRound = nearestPowerOf2(numCompetitors);
    let totalRounds = getBaseLog(2, numCompetitors);

    if (largestRound !== numCompetitors) {
      totalRounds += 1;
    }

    // Create bracket structure
    const newBracket = {
      ...newGameState.bracket,
      bracket:
        totalRounds > 0
          ? new Array(totalRounds)
              .fill(null)
              .map((_, index) => {
                // Round 1
                if (index === 0) {
                  return Array.from(
                    {
                      length:
                        numCompetitors % 2 === 0
                          ? numCompetitors / 2
                          : (numCompetitors + 1) / 2,
                    },
                    () => ({
                      id: 0,
                      position: {
                        top: 130,
                        left: 5,
                      },
                      competitors: [],
                      isSingle: true,
                      isDouble: true,
                      number: 1,
                    })
                  );
                }
                return [];
              })
              .filter((round) => round.length !== 0)
          : [],
      totalCompetitors: numCompetitors,
    };

    // Initialize roundValues array with auto-populated names and copied settings
    const roundValues: RoundState[] = [];
    for (let i = 0; i < totalRounds; i++) {
      // Check if we already have a round value for this index
      const existingRound = newGameState.bracket?.roundValues?.[i];
      if (existingRound && existingRound.bracketName) {
        // Keep existing round with its name and settings
        roundValues.push(existingRound);
      } else {
        // Create new round with auto-populated name and copy settings from previous round
        const previousRound = i > 0 ? roundValues[i - 1] : undefined;
        roundValues.push(
          createDefaultRoundState(i, previousRound) as RoundState
        );
      }
    }

    // Add the roundValues to the new bracket
    newBracket.roundValues = roundValues;

    // Initialize competitors array if not already populated
    const newCompetitors =
      newGameState.competitors?.length > 0
        ? newGameState.competitors
        : Array.from({ length: numCompetitors }, () => '');

    console.log('Updating bracket state:', {
      totalRounds,
      competitors: newCompetitors.length,
      bracketLength: newBracket.bracket.length,
      roundValuesLength: newBracket.roundValues.length,
    });

    // Update the state
    onChangeNewGameState({
      ...newGameState,
      bracket: newBracket,
      competitors: newCompetitors,
      totalRounds,
      currentRound: 1,
      contestType: 'bracket_manual', // Ensure contestType is set to bracket_manual
    });
  };

  // Add console logging to check current state
  const handleCompetitorsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    if (isNaN(Number(newValue))) return;

    const numCompetitors = Number(newValue);
    setupBracketData(numCompetitors);
  };

  return (
    <ViewContainer>
      <SectionTitle>Contestants</SectionTitle>
      <InputBodyContainer>
        <CustomInputLabel># of contestants</CustomInputLabel>
        <CustomInputContainer>
          <LeftSection>
            <IconContainer>
              <GamifyIcon
                icon={'users'}
                color={'#FF6A00'}
                backgroundColor={'#FFE8E0'}
                height={24}
                width={24}
              />
            </IconContainer>
            <ContestantNumber
              placeholder="e.g 10"
              value={
                newGameState.bracket?.totalCompetitors
                  ? String(newGameState.bracket.totalCompetitors)
                  : ''
              }
              onChange={handleCompetitorsChange}
            />
          </LeftSection>
          {newGameState.totalRounds > 0 &&
            newGameState.bracket?.totalCompetitors > 0 && (
              <RightSection>
                ({newGameState.totalRounds} Round
                {newGameState.totalRounds > 1 ? 's' : ''})
                <svg
                  width="8"
                  height="14"
                  viewBox="0 0 8 14"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M1 13L7 7L1 1"
                    stroke="#AAAAAA"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </RightSection>
            )}
        </CustomInputContainer>
      </InputBodyContainer>
    </ViewContainer>
  );
};

function getBaseLog(x: number, y: number) {
  return Math.floor(Math.log(y) / Math.log(x));
}

function nearestPowerOf2(n: number) {
  return 1 << (31 - Math.clz32(n));
}

export default BracketView;
